import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { B } from "../../../../components/UI";
import useAutoSaveDraft from "../../../../hooks/useAutoSaveDraft";
import { getGSTValidationMessage, getMobileValidationMessage } from "../../../../utils/validation";
import { getTransportDisplayName, findTransportById } from "../../../../utils/transport";

export const parseDesignPrice = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;
  const normalized = value.replace(/,/g, "").trim();
  const match = normalized.match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : 0;
};

// Next incremental "manual" design number (1, 2, 3, ...), matching the behaviour
// of the mobile "+ Add Design" button. Rows linked to a real cached design are
// excluded from the count so manual numbering stays independent.
const computeNextManualDesignNumber = (rowList) => {
  const nums = (rowList || [])
    .map(r => (r.designId ? NaN : Number(String(r.designQuery || "").trim())))
    .filter(Number.isFinite);
  return String((nums.length ? Math.max(...nums) : 0) + 1);
};

export const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getFinancialYearStartYear = (date = new Date()) => {
  const month = date.getMonth() + 1; // 1-12
  const year = date.getFullYear();
  return month >= 4 ? year : year - 1;
};

const isSameFinancialYear = (date, fyStartYear) => {
  const d = typeof date?.toDate === "function" ? date.toDate() : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  return getFinancialYearStartYear(d) === fyStartYear;
};

export const getCustomerLabel = (customer, includeFirm = true) =>
  `${customer.customer_name || "Unnamed Customer"}${(includeFirm && customer.firm_name) ? ` — ${customer.firm_name}` : ""}${customer.mobile_number ? ` (${customer.mobile_number})` : ""}`;

export const getDesignLabel = (design) => {
  if (!design) return "";
  return `${design.design_number || "No Design No"}${design.active ? "" : " (OOS)"}`;
};

export const getDesignImage = (design) => design?.imageUrl || design?.images?.[0] || "";
const getQuotationDiscountPercentage = (quotation) => Number(quotation?.discount_percentage ?? 0) || 0;
const getQuotationPackagingCharges = (quotation) => Number(quotation?.packaging_charges ?? 0) || 0;
const getQuotationTransportCharges = (quotation) => Number(quotation?.transport_charges ?? 0) || 0;
const getQuotationRoundOffAmount = (quotation) => Number(quotation?.round_off_amount ?? 0) || 0;
const getTaxBreakdown = ({ billNumber, customerState, grossAmount }) => {
  const taxableAmount = Math.max(0, Number(grossAmount) || 0);
  const normalizedBillNumber = String(billNumber || "").trim();
  const normalizedState = String(customerState || "").trim().toLowerCase();
  const hasBillNumber = Boolean(normalizedBillNumber);
  const isGujarat = normalizedState === "gujarat";

  if (!hasBillNumber || taxableAmount <= 0) {
    return { cgstAmount: 0, sgstAmount: 0, igstAmount: 0, totalTaxAmount: 0 };
  }

  if (isGujarat) {
    const cgstAmount = taxableAmount * 0.025;
    const sgstAmount = taxableAmount * 0.025;
    return { cgstAmount, sgstAmount, igstAmount: 0, totalTaxAmount: cgstAmount + sgstAmount };
  }

  const igstAmount = taxableAmount * 0.05;
  return { cgstAmount: 0, sgstAmount: 0, igstAmount, totalTaxAmount: igstAmount };
};
// Normalises a stored default-discount setting into a usable 0-100 percentage.
// Minimum order value (in ₹) below which the auto discount is not applied.
// 0 means "no minimum" — the auto discount is then always eligible.
const parseAmountSetting = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

const parseDiscountSetting = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(100, parsed);
};

const QUOTATION_DRAFT_KEY = "quotation_draft";
export const toTitleCase = (value) =>
  String(value || "").replace(/\b\w/g, char => char.toUpperCase());

export const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

export const formatLastSavedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDateInputValue = (value) => {
  if (!value) return "";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 10);
};

const computeQuotationTotals = ({
  rows,
  designMap,
  allowPriceEdit,
  discountPercentageValue,
  packagingChargeValue,
  transportChargeValue,
  billNumber,
  customerState,
  allowDiscount,
  showPackagingCharges,
  initialQuotation,
}) => {
  const computedItems = rows.map((row, index) => {
    const design = designMap.get(row.designId);
    const quantity = Math.max(0, Number(row.quantity) || 0);
    const defaultPrice = parseDesignPrice(design?.price);
    const price = allowPriceEdit ? parseDesignPrice(row.unitPrice) : defaultPrice;
    const subtotal = price * quantity;
    return {
      serial: index + 1,
      designId: row.designId,
      designNo: (design?.design_number || String(row.designQuery || "").trim() || "—"),
      quantity,
      price,
      subtotal,
      imageUrl: getDesignImage(design),
    };
  });

  const subtotalAmount = computedItems.reduce((sum, item) => sum + item.subtotal, 0);
  const totalQuantity = computedItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const discountPercentage = Number(discountPercentageValue) || 0;
  const shouldApplyDiscount = allowDiscount || discountPercentage > 0;
  const discountAmount = shouldApplyDiscount ? (subtotalAmount * discountPercentage) / 100 : 0;
  const packagingCharges = showPackagingCharges ? Math.max(0, Number(packagingChargeValue) || 0) : 0;
  const transportCharges = Math.max(0, Number(transportChargeValue) || 0);
  const grossAmount = Math.max(0, subtotalAmount - discountAmount);
  const { cgstAmount, sgstAmount, igstAmount, totalTaxAmount } = getTaxBreakdown({
    billNumber,
    customerState,
    grossAmount,
  });
  const rawGrandTotal = Math.max(0, grossAmount + packagingCharges + transportCharges + totalTaxAmount);
  const grandTotal = Math.round(rawGrandTotal);
  const roundOffAmount = +(grandTotal - rawGrandTotal).toFixed(2);

  return {
    computedItems,
    subtotalAmount,
    totalQuantity,
    discountPercentage,
    shouldApplyDiscount,
    discountAmount,
    packagingCharges,
    transportCharges,
    grossAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTaxAmount,
    rawGrandTotal,
    grandTotal,
    roundOffAmount,
  };
};

// Quotation builder logic extracted from Dashboard into a single hook shared by
// the mobile and desktop render components. Returns every state value, setter,
// derived value, and handler the two layouts need.
export function useQuotationBuilder({ customers, transports = [], designs, quotations = [], onSave, saving, initialQuotation = null, onCancelEdit, readOnly = false, quotationSettings = { allowPriceEdit: false, allowDiscount: false, allowDiscountInvoice: false, defaultDiscountQuotation: 0, defaultDiscountInvoice: 0, minOrderValueQuotation: 0, minOrderValueInvoice: 0 }, showToast, onBack, confirm }) {
  const [customerLookup, setCustomerLookup] = useState("");
  const [customerForm, setCustomerForm] = useState({
    id: "",
    customer_name: "",
    firm_name: "",
    mobile_number: "",
    city: "",
    state: "",
    pincode: "",
    address: "",
    transport_name: "",
    transport_id: "",
    transport_gst: "",
    gst_number: "",
    status: "active",
  });
  const [quotationNumber, setQuotationNumber] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [createdAt, setCreatedAt] = useState(() => getDateInputValue(new Date()));
  const [rows, setRows] = useState([{ uid: Date.now(), designId: "", designQuery: computeNextManualDesignNumber([]), quantity: "", unitPrice: "" }]);
  const [discountPercentageValue, setDiscountPercentageValue] = useState("");
  const [packagingChargeValue, setPackagingChargeValue] = useState("");
  const [transportChargeValue, setTransportChargeValue] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [pendingFocusRowUid, setPendingFocusRowUid] = useState(null);
  const [sendWhatsApp, setSendWhatsApp] = useState(Boolean(quotationSettings?.autoSendWhatsApp));
  const [gstError, setGstError] = useState("");
  const [gstFocused, setGstFocused] = useState(false);
  const [showCustomerSheet, setShowCustomerSheet] = useState(false);
  const [showDesignSearch, setShowDesignSearch] = useState(false);
  const [designSheetMode, setDesignSheetMode] = useState("search");
  const [designSheetRowUid, setDesignSheetRowUid] = useState(null);
  const [editingDesignRowUid, setEditingDesignRowUid] = useState(null);
  const [designSearchTerm, setDesignSearchTerm] = useState("");
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const [manualDesignValue, setManualDesignValue] = useState("");
  const designInputRefs = useRef(new Map());
  const quantityInputRefs = useRef(new Map());

  // A quotation is a complex object spread across several controlled fields.
  // Keep the persisted draft shape explicit so restore does not include UI-only
  // state such as image previews or focus management.
  const quotationDraftState = useMemo(() => ({
    quotationNumber,
    billNumber,
    createdAt,
    customerLookup,
    customerForm,
    rows,
    discountPercentageValue,
    packagingChargeValue,
    transportChargeValue,
  }), [quotationNumber, billNumber, createdAt, customerLookup, customerForm, rows, discountPercentageValue, packagingChargeValue, transportChargeValue]);

  const restoreQuotationDraftState = (draft = {}) => {
    if (!draft || typeof draft !== "object") return;

    if (typeof draft.quotationNumber === "string") setQuotationNumber(draft.quotationNumber);
    if (typeof draft.billNumber === "string") setBillNumber(draft.billNumber);
    if (typeof draft.createdAt === "string") setCreatedAt(draft.createdAt);
    if (typeof draft.customerLookup === "string") setCustomerLookup(draft.customerLookup);
    if (draft.customerForm && typeof draft.customerForm === "object") {
      setCustomerForm(prev => ({ ...prev, ...draft.customerForm }));
    }
    if (Array.isArray(draft.rows) && draft.rows.length) {
      setRows(draft.rows.map((row, index) => ({
        uid: row.uid || Date.now() + index + Math.random(),
        designId: row.designId || "",
        designQuery: row.designQuery || "",
        quantity: row.quantity ?? "",
        unitPrice: row.unitPrice ?? "",
      })));
    }
    if (typeof draft.discountPercentageValue === "string" || typeof draft.discountPercentageValue === "number") {
      const restoredDiscount = String(draft.discountPercentageValue).trim();
      if (restoredDiscount !== "") {
        const isBillMode = Boolean(String(draft.billNumber || "").trim());
        const defaultPercent = isBillMode ? defaultDiscountInvoice : defaultDiscountQuotation;
        // Only treat as manually touched if it differs from the auto-applied default;
        // otherwise leave it auto-manageable so it can be removed below threshold.
        if (String(defaultPercent) !== restoredDiscount) {
          manualDiscountTouchedRef.current = true;
        }
      }
      setDiscountPercentageValue(restoredDiscount);
    }
    if (typeof draft.packagingChargeValue === "string" || typeof draft.packagingChargeValue === "number") {
      setPackagingChargeValue(String(draft.packagingChargeValue));
    }
    if (typeof draft.transportChargeValue === "string" || typeof draft.transportChargeValue === "number") {
      setTransportChargeValue(String(draft.transportChargeValue));
    }
  };


  const allowPriceEdit = Boolean(quotationSettings?.allowPriceEdit);
  // Quotations and invoices share this builder; a document is an invoice when
  // it has a bill number. Each document type has its own discount permission so
  // enabling one never leaks into the other.
  const isInvoice = Boolean(String(billNumber || "").trim());
  const allowDiscount = isInvoice
    ? Boolean(quotationSettings?.allowDiscountInvoice)
    : Boolean(quotationSettings?.allowDiscount);
  const showFirmName = Boolean(quotationSettings?.showFirmName);
  const showPackagingCharges = Boolean(quotationSettings?.showPackagingCharges);
  // When this is off, entering a design number is treated as a manual entry and
  // never matches against the cached design catalogue (no image/price fetch).
  const fetchDesignDetails = Boolean(quotationSettings?.fetchDesignDetails);

  const defaultDiscountQuotation = parseDiscountSetting(quotationSettings?.defaultDiscountQuotation);
  const defaultDiscountInvoice = parseDiscountSetting(quotationSettings?.defaultDiscountInvoice);
  const minOrderValueQuotation = parseAmountSetting(quotationSettings?.minOrderValueQuotation);
  const minOrderValueInvoice = parseAmountSetting(quotationSettings?.minOrderValueInvoice);
  // The threshold that applies to the document currently being built.
  const minOrderValue = isInvoice ? minOrderValueInvoice : minOrderValueQuotation;

  // Auto-apply of the configured default discount percentage. A quotation and
  // its invoice (same form, once a bill number is entered) can have different
  // defaults, so the relevant one is applied whenever the bill-mode flips. Any
  // discount typed by the admin (or restored with values from a draft/edit)
  // marks the field as touched and is never overwritten.
  const manualDiscountTouchedRef = useRef(false);
  const autoDiscountAppliedRef = useRef("");
  const setDiscountPercentageValueManual = (value) => {
    manualDiscountTouchedRef.current = true;
    setDiscountPercentageValue(value);
  };
  // Only called while the field is untouched, so clearing is safe. The value
  // tracks the relevant default for the current document type: it is cleared
  // when that type's discount flag is off or its default is unset, keeping the
  // two document types fully independent.

  const designMap = useMemo(() => new Map(designs.map(design => [design.id, design])), [designs]);
  const availableCustomers = useMemo(
    () => customers.filter(customer => customer.status !== "deleted"),
    [customers]
  );

  const nextQuotationNumber = useMemo(() => {
    const lastNumber = quotations.reduce((maxNumber, quotation) => {
      const value = Number(String(quotation?.quotation_number || "").trim());
      if (!Number.isFinite(value)) return maxNumber;
      return Math.max(maxNumber, Math.trunc(value));
    }, 0);
    return String(lastNumber + 1);
  }, [quotations]);

  const updateRow = (uid, field, value) => {
    setRows(prev => prev.map(row => (row.uid === uid ? { ...row, [field]: value } : row)));
  };

  const addRow = () => {
    const newRowUid = Date.now() + Math.random();
    setRows(prev => [...prev, { uid: newRowUid, designId: "", designQuery: computeNextManualDesignNumber(prev), quantity: "", unitPrice: "" }]);
    setPendingFocusRowUid(newRowUid);
  };

  const addRowAfter = (uid) => {
    const newRowUid = Date.now() + Math.random();
    setRows(prev => {
      const nextRow = { uid: newRowUid, designId: "", designQuery: computeNextManualDesignNumber(prev), quantity: "", unitPrice: "" };
      const rowIndex = prev.findIndex(row => row.uid === uid);
      if (rowIndex === -1 || rowIndex === prev.length - 1) return [...prev, nextRow];
      const nextRows = [...prev];
      nextRows.splice(rowIndex + 1, 0, nextRow);
      return nextRows;
    });
    setPendingFocusRowUid(newRowUid);
  };

  const removeRow = (uid) => {
    setRows(prev => (prev.length === 1 ? [{ uid: Date.now(), designId: "", designQuery: computeNextManualDesignNumber([]), quantity: "", unitPrice: "" }] : prev.filter(row => row.uid !== uid)));
  };

  const {
    computedItems,
    subtotalAmount,
    totalQuantity,
    discountPercentage,
    shouldApplyDiscount,
    discountAmount,
    packagingCharges,
    transportCharges,
    grossAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTaxAmount,
    grandTotal,
    roundOffAmount,
  } = useMemo(() => computeQuotationTotals({
    rows,
    designMap,
    allowPriceEdit,
    discountPercentageValue,
    packagingChargeValue,
    transportChargeValue,
    billNumber,
    customerState: customerForm.state,
    allowDiscount,
    showPackagingCharges,
    initialQuotation,
  }), [
    rows,
    designMap,
    allowPriceEdit,
    discountPercentageValue,
    packagingChargeValue,
    transportChargeValue,
    billNumber,
    customerForm.state,
    allowDiscount,
    showPackagingCharges,
    initialQuotation,
  ]);
  const applyRelevantDefaultDiscount = useCallback((isBillMode) => {
    const defaultPercent = isBillMode ? defaultDiscountInvoice : defaultDiscountQuotation;
    const threshold = isBillMode ? minOrderValueInvoice : minOrderValueQuotation;
    // The auto discount is only applied when the order value reaches the
    // configured minimum order value for this document type.
    const isEligible = threshold <= 0 || subtotalAmount >= threshold;
    const nextValue = allowDiscount && defaultPercent > 0 && isEligible ? String(defaultPercent) : "";
    setDiscountPercentageValue(nextValue);
    autoDiscountAppliedRef.current = nextValue;
  }, [allowDiscount, defaultDiscountInvoice, defaultDiscountQuotation, minOrderValueInvoice, minOrderValueQuotation, subtotalAmount]);

  const setCustomerField = (field, value) => {
    setCustomerForm(prev => ({ ...prev, [field]: value }));
  };

  const draftRestoredRef = useRef(false);

  const { isDraftRestored, lastSavedAt, clearDraft } = useAutoSaveDraft(
    QUOTATION_DRAFT_KEY,
    quotationDraftState,
    restoreQuotationDraftState,
    { enabled: !readOnly && !initialQuotation, debounceMs: 500 }
  );
  const formattedLastSavedAt = formatLastSavedAt(lastSavedAt);

  const showDraftBanner = isDraftRestored && !readOnly && !initialQuotation;
  const showAutoSaveIndicator = formattedLastSavedAt && !initialQuotation && !readOnly;

  // Keep a ref so the initialisation useEffect below can check whether the
  // draft-auto-restore already populated the form fields and skip overwriting
  // them with empty defaults.
  if (isDraftRestored && !draftRestoredRef.current) {
    draftRestoredRef.current = true;
  }

  // Auto-apply the relevant default discount on fresh forms and whenever the
  // document flips between quotation (no bill number) and invoice (bill number
  // present). Skipped once the admin has entered their own value, when an
  // existing document is being edited, and in read-only mode.
  useEffect(() => {
    if (readOnly || manualDiscountTouchedRef.current) return;
    if (initialQuotation) {
      // Edit mode: auto-remove the saved discount when the subtotal falls
      // below the minimum order value threshold.
      const isBillMode = Boolean(String(billNumber || "").trim());
      const threshold = isBillMode ? minOrderValueInvoice : minOrderValueQuotation;
      const isEligible = threshold <= 0 || subtotalAmount >= threshold;
      if (!isEligible && discountPercentageValue) {
        setDiscountPercentageValue("");
      }
      return;
    }
    // Fresh / draft-restored form: auto-apply or auto-remove the default
    // discount based on eligibility.
    applyRelevantDefaultDiscount(Boolean(String(billNumber || "").trim()));
  }, [readOnly, manualDiscountTouchedRef, initialQuotation, billNumber, applyRelevantDefaultDiscount, discountPercentageValue, subtotalAmount, minOrderValueInvoice, minOrderValueQuotation]);

  const applyCustomer = (customer = null) => {
    if (!customer) {
      setCustomerForm({
        id: "",
        customer_name: "",
        firm_name: "",
        mobile_number: "",
        city: "",
        state: "",
        pincode: "",
        address: "",
        transport_name: "",
        transport_id: "",
        transport_gst: "",
        gst_number: "",
        status: "active",
      });
      return;
    }

    const linkedTransport = customer.transport_id ? findTransportById(transports, customer.transport_id) : null;
    setCustomerForm({
      id: customer.id || "",
      customer_name: toTitleCase(customer.customer_name || ""),
      firm_name: toTitleCase(customer.firm_name || ""),
      mobile_number: customer.mobile_number || "",
      city: toTitleCase(customer.city || ""),
      state: customer.state || "",
      pincode: customer.pincode || "",
      address: toTitleCase(customer.address || ""),
      transport_name: linkedTransport ? getTransportDisplayName(linkedTransport) : toTitleCase(customer.transport_name || ""),
      transport_id: linkedTransport ? linkedTransport.id : (customer.transport_id || ""),
      transport_gst: linkedTransport ? (linkedTransport.gst || "") : (customer.transport_gst || ""),
      gst_number: customer.gst_number || "",
      status: customer.status || "active",
    });
  };

  useEffect(() => {
    // When a draft was restored from localStorage, skip the default-reset
    // branch so the restored fields survive the initialisation effect.
    if (!initialQuotation && draftRestoredRef.current) return;

    if (!initialQuotation) {
      setQuotationNumber(nextQuotationNumber);
      setCustomerLookup("");
      setBillNumber("");
      setCreatedAt(getDateInputValue(new Date()));
      applyCustomer(null);
      setRows([{ uid: Date.now(), designId: "", designQuery: computeNextManualDesignNumber([]), quantity: "", unitPrice: "" }]);
      setDiscountPercentageValue("");
      setPackagingChargeValue("");
      setTransportChargeValue("");
      return;
    }
    setQuotationNumber(initialQuotation.quotation_number || "");
    setBillNumber(initialQuotation.bill_number || "");
    setCreatedAt(getDateInputValue(initialQuotation.created_at) || getDateInputValue(new Date()));
    const currentCustomer = availableCustomers.find(customer => customer.id === initialQuotation.customer_id);
    if (currentCustomer) {
      setCustomerLookup(getCustomerLabel(currentCustomer, showFirmName));
      applyCustomer(currentCustomer);
    } else {
      setCustomerLookup(initialQuotation.customer_name || "");
      setCustomerForm({
        id: initialQuotation.customer_id || "",
        customer_name: initialQuotation.customer_name || "",
        firm_name: "",
        mobile_number: initialQuotation.customer_mobile || "",
        city: initialQuotation.customer_city || "",
        state: initialQuotation.customer_state || "",
        pincode: initialQuotation.customer_pincode || "",
        address: "",
        transport_name: initialQuotation.transport_name || "",
        transport_id: initialQuotation.transport_id || "",
        transport_gst: (() => { const t = findTransportById(transports, initialQuotation.transport_id); return t ? (t.gst || "") : ""; })(),
        gst_number: initialQuotation.customer_gst || "",
        status: "active",
      });
    }
    const nextRows = (initialQuotation.items || []).length
      ? initialQuotation.items.map((item, index) => ({
        uid: Date.now() + index + Math.random(),
        designId: item.design_id || "",
        designQuery: getDesignLabel(designMap.get(item.design_id)) || item.design_no || "",
        quantity: item.quantity ?? "",
        unitPrice: item.price ?? parseDesignPrice(designMap.get(item.design_id)?.price),
      }))
      : [{ uid: Date.now(), designId: "", designQuery: computeNextManualDesignNumber([]), quantity: "", unitPrice: "" }];
    setRows(nextRows);
    const savedDiscountPercent = Number(initialQuotation.discount_percentage || 0);
    const restoredDiscountPercent = savedDiscountPercent > 0
      ? savedDiscountPercent
      : getQuotationDiscountPercentage(initialQuotation);
    setDiscountPercentageValue(String(restoredDiscountPercent > 0 ? restoredDiscountPercent : ""));
    setPackagingChargeValue(String(getQuotationPackagingCharges(initialQuotation) || ""));
    setTransportChargeValue(String(getQuotationTransportCharges(initialQuotation) || ""));
  }, [initialQuotation, availableCustomers, designMap, nextQuotationNumber]);

  const getIncrementedQuotationNumber = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return nextQuotationNumber;
    const numericValue = Number(normalized);
    if (!Number.isFinite(numericValue)) return nextQuotationNumber;
    return String(Math.trunc(numericValue) + 1);
  };

  const resetQuotationForm = ({ clearSavedDraft = false, incrementQuotationNumber = false } = {}) => {
    if (clearSavedDraft) clearDraft();
    setQuotationNumber(
      incrementQuotationNumber
        ? getIncrementedQuotationNumber(quotationNumber)
        : nextQuotationNumber
    );
    setCustomerLookup("");
    setBillNumber("");
    setCreatedAt(getDateInputValue(new Date()));
    applyCustomer(null);
    setRows([{ uid: Date.now(), designId: "", designQuery: computeNextManualDesignNumber([]), quantity: "", unitPrice: "" }]);
    manualDiscountTouchedRef.current = false;
    autoDiscountAppliedRef.current = "";
    setDiscountPercentageValue("");
    setPackagingChargeValue("");
    setTransportChargeValue("");
  };

  const handleClearQuotation = async () => {
    if (readOnly) return;

    const message = "Clear this quotation and remove the saved draft? This cannot be undone.";
    // Prefer the styled app confirm modal; fall back to the native box if it's not available.
    const confirmed = confirm
      ? await confirm({ title: "Clear quotation?", message, confirmLabel: "Clear", cancelLabel: "Cancel" })
      : window.confirm(message);
    if (!confirmed) return;

    resetQuotationForm({ clearSavedDraft: true });
  };

  useEffect(() => {
    if (!pendingFocusRowUid) return;
    const rafId = window.requestAnimationFrame(() => {
      const targetInput = quantityInputRefs.current.get(pendingFocusRowUid);
      if (targetInput) {
        targetInput.focus();
      }
      setPendingFocusRowUid(null);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [rows, pendingFocusRowUid]);

  const handleCustomerQueryChange = (value) => {
    setCustomerLookup(value);
    const matchedCustomer = availableCustomers.find(customer => getCustomerLabel(customer, showFirmName) === value);
    if (matchedCustomer) {
      applyCustomer(matchedCustomer);
      return;
    }
    setCustomerForm(prev => ({ ...prev, id: "", customer_name: toTitleCase(value) }));
  };

  const nextManualDesignNumber = useMemo(() => computeNextManualDesignNumber(rows), [rows]);

  const commitManualDesign = (uid, value) => {
    if (readOnly) return;
    updateRow(uid, "designQuery", String(value || "").trim());
    updateRow(uid, "designId", "");
    setShowDesignSearch(false);
    setDesignSheetMode("search");
  };

  const handleDesignQueryChange = (uid, value) => {
    // Only match the typed number against the cached design catalogue when the
    // "Auto-fetch design info in quotation" setting is enabled. When disabled the
    // value is stored as a plain manual design number (no image, no auto price).
    const matchedDesign = fetchDesignDetails
      ? designs.find(design => getDesignLabel(design) === value || String(design.design_number || "").trim() === String(value || "").trim())
      : null;
    setRows(prev => prev.map(row => {
      if (row.uid !== uid) return row;
      const nextRow = { ...row, designQuery: value, designId: matchedDesign?.id || "" };
      if (matchedDesign && allowPriceEdit) {
        nextRow.unitPrice = parseDesignPrice(matchedDesign.price);
      }
      return nextRow;
    }));
  };

  const selectDesignForRow = (uid, design) => {
    if (!design || readOnly) return;
    setRows(prev => prev.map(row => {
      if (row.uid !== uid) return row;
      const nextRow = {
        ...row,
        designId: design.id || "",
        designQuery: getDesignLabel(design),
        quantity: Number(row.quantity) > 0 ? row.quantity : 1,
      };
      if (allowPriceEdit) nextRow.unitPrice = parseDesignPrice(design.price);
      return nextRow;
    }));
    setShowDesignSearch(false);
    setDesignSearchTerm("");
    setDesignSheetMode("search");
  };

  const handleAddDesignMobile = () => {
    if (readOnly) return;
    const number = nextManualDesignNumber;
    const emptyRow = rows.find(row => !row.designId && !String(row.designQuery || "").trim());
    if (emptyRow) {
      updateRow(emptyRow.uid, "designQuery", number);
      updateRow(emptyRow.uid, "designId", "");
    } else {
      setRows(prev => [...prev, { uid: Date.now() + Math.random(), designId: "", designQuery: number, quantity: "", unitPrice: "" }]);
    }
  };

  const openDesignSheetForRow = (uid) => {
    setDesignSearchTerm("");
    setDesignSheetMode("search");
    setDesignSheetRowUid(uid);
    setShowDesignSearch(true);
  };

  const handleSelectDesignMobile = () => {
    if (readOnly) return;
    const emptyRow = rows.find(row => !row.designId && !String(row.designQuery || "").trim());
    if (emptyRow) {
      openDesignSheetForRow(emptyRow.uid);
      return;
    }
    const uid = Date.now() + Math.random();
    setRows(prev => [...prev, { uid, designId: "", designQuery: "", quantity: "", unitPrice: "" }]);
    openDesignSheetForRow(uid);
  };

  const handleEditDesignRow = (uid) => {
    if (readOnly) return;
    setEditingDesignRowUid(prev => (prev === uid ? null : uid));
  };

  const setDesignEditNumber = (uid, value) => {
    updateRow(uid, "designQuery", value);
    updateRow(uid, "designId", "");
  };

  const finishDesignEdit = (uid) => {
    setEditingDesignRowUid(prev => (prev === uid ? null : prev));
  };

  const handleDesignRowEnter = (event, uid) => {
    if (event.key !== "Enter" || readOnly) return;
    event.preventDefault();
    addRowAfter(uid);
  };

  const handleGenerateBillNumber = async () => {
    if (readOnly) return;
    const fyStartYear = getFinancialYearStartYear();
    const maxBillNumber = quotations.reduce((max, quotation) => {
      const rawBillNumber = String(quotation?.bill_number || "").trim();
      if (!rawBillNumber) return max;
      if (!isSameFinancialYear(quotation?.created_at, fyStartYear)) return max;
      const numericMatch = rawBillNumber.match(/\d+/);
      if (!numericMatch) return max;
      const numericValue = Number(numericMatch[0]);
      if (!Number.isFinite(numericValue)) return max;
      return Math.max(max, numericValue);
    }, 0);
    const nextBillNumber = String(maxBillNumber + 1);
    const message = `Confirm with manually created bills, is bill number ${nextBillNumber} valid?`;
    // Prefer the styled app confirm modal; fall back to the native box if it's not available.
    const confirmed = confirm
      ? await confirm({ title: "Confirm bill number", message, confirmLabel: "Yes", cancelLabel: "No" })
      : window.confirm(message);
    if (!confirmed) return;
    setBillNumber(nextBillNumber);
  };

  const handleSaveQuotation = () => {
    if (readOnly) return;

    const normalizedBillNumber = String(billNumber || "").trim();
    const isBill = Boolean(normalizedBillNumber);
    const normalizedCustomerName = String(customerForm.customer_name || "").trim();
    const normalizedQuotationNumber = String(quotationNumber || "").trim();

    // Common required fields for both bills and quotations.
    if (!normalizedCustomerName) {
      showToast?.("Customer name is required", B.red);
      return;
    }
    if (!normalizedQuotationNumber) {
      showToast?.("Quotation number is required", B.red);
      return;
    }
    
    const mobileMessage = getMobileValidationMessage(customerForm.mobile_number);
    if (mobileMessage) {
      showToast?.(mobileMessage, B.red);
      return;
    }

    // Bill-specific required fields.
    if (isBill) {
      if (!customerForm.state) {
        showToast?.("State is mandatory when a bill number is entered.", B.red);
        return;
      }
      const normalizedGst = String(customerForm.gst_number || "").trim();
      if (!normalizedGst) {
        showToast?.("GST number is required for a bill.", B.red);
        return;
      }
      const gstMessage = getGSTValidationMessage(normalizedGst);
      if (gstMessage) {
        setGstError(gstMessage);
        showToast?.(gstMessage, B.red);
        return;
      }
    }

    const validItems = computedItems.filter(item => item.designId || String(item.designNo || "").trim());
    const invalidItems = validItems.filter(item => item.quantity <= 0 || item.price <= 0);
    if (invalidItems.length) {
      showToast?.("Every design item must have quantity greater than 0 and price greater than 0.", B.red);
      return;
    }
    const [year, month, day] = (createdAt || "").split("-").map(Number);
    const now = new Date();

    // Preserve the original created_at time-of-day when editing, so saving an
    // update to a quotation does not silently change its created time (which
    // would also re-sort it to the top of the list).
    let hours = now.getHours();
    let minutes = now.getMinutes();
    let seconds = now.getSeconds();
    if (initialQuotation?.created_at) {
      const original = typeof initialQuotation.created_at?.toDate === "function"
        ? initialQuotation.created_at.toDate()
        : new Date(initialQuotation.created_at);
      if (!Number.isNaN(original.getTime())) {
        hours = original.getHours();
        minutes = original.getMinutes();
        seconds = original.getSeconds();
      }
    }

    const quotationDate = year && month && day
      ? new Date(year, month - 1, day, hours, minutes, seconds)
      : new Date();
    onSave?.({
      quotationId: initialQuotation?.id || null,
      quotationNumber,
      billNumber,
      createdAt: quotationDate,
      customer: customerForm,
      customer_gst: customerForm.gst_number,
      items: validItems,
      subtotalAmount,
      discountAmount,
      discountPercentage,
      packagingCharges,
      transportCharges,
      grossAmount,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTaxAmount,
      grandTotal,
      roundOffAmount,
      sendWhatsApp,
      reset: () => {
        resetQuotationForm({ clearSavedDraft: true, incrementQuotationNumber: true });
      },
    });
  };

  const mobileAutoSaveText = lastSavedAt
    ? new Date(lastSavedAt).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })
    : "";
  const selectedCustomer = Boolean(customerForm.id || customerForm.customer_name || customerForm.mobile_number);
  const filledItemCount = computedItems.filter(item => item.designId || String(item.designNo || "").trim() !== "—").length;
  const designSearchResults = designs
    .filter(design => {
      const term = designSearchTerm.trim().toLowerCase();
      if (!term) return true;
      return [design.design_number, design.name, design.product_name, design.category]
        .some(value => String(value || "").toLowerCase().includes(term));
    })
    .slice(0, 30);
  const nextMobileDesignRow = rows.find(row => !row.designId && !String(row.designQuery || "").trim()) || rows[rows.length - 1];

  return {
    // Props passed back for render convenience
    customers,
    transports,
    designs,
    quotations,
    initialQuotation,
    readOnly,
    saving,
    onSave,
    onBack,
    onCancelEdit,

    // Customer fields
    customerLookup,
    setCustomerLookup,
    customerForm,
    setCustomerForm,
    setCustomerField,
    applyCustomer,
    availableCustomers,
    handleCustomerQueryChange,
    selectedCustomer,

    // Quotation meta
    quotationNumber,
    setQuotationNumber,
    billNumber,
    setBillNumber,
    isInvoice,
    createdAt,
    setCreatedAt,
    nextQuotationNumber,

    // Items / rows
    rows,
    updateRow,
    addRow,
    addRowAfter,
    removeRow,
    designMap,
    handleDesignQueryChange,
    selectDesignForRow,
    commitManualDesign,
    handleAddDesignMobile,
    handleSelectDesignMobile,
    handleEditDesignRow,
    setDesignEditNumber,
    finishDesignEdit,
    handleDesignRowEnter,
    designInputRefs,
    quantityInputRefs,
    nextManualDesignNumber,
    nextMobileDesignRow,
    manualDesignValue,
    setManualDesignValue,
    showDesignSearch,
    setShowDesignSearch,
    designSheetMode,
    setDesignSheetMode,
    designSheetRowUid,
    setDesignSheetRowUid,
    editingDesignRowUid,
    setEditingDesignRowUid,
    designSearchTerm,
    setDesignSearchTerm,
    designSearchResults,
    previewImageUrl,
    setPreviewImageUrl,

    // Pricing / discount / totals
    discountPercentageValue,
    setDiscountPercentageValue: setDiscountPercentageValueManual,
    packagingChargeValue,
    setPackagingChargeValue,
    transportChargeValue,
    setTransportChargeValue,
    allowPriceEdit,
    allowDiscount,
    minOrderValue,
    minOrderValueQuotation,
    minOrderValueInvoice,
    autoDiscountEligible: minOrderValue <= 0 || subtotalAmount >= minOrderValue,
    showFirmName,
    showPackagingCharges,
    computedItems,
    subtotalAmount,
    totalQuantity,
    discountPercentage,
    shouldApplyDiscount,
    discountAmount,
    packagingCharges,
    transportCharges,
    grossAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTaxAmount,
    grandTotal,
    roundOffAmount,

    // GST
    gstError,
    setGstError,
    gstFocused,
    setGstFocused,

    // More options / sheets
    moreOptionsOpen,
    setMoreOptionsOpen,
    showCustomerSheet,
    setShowCustomerSheet,

    // WhatsApp
    sendWhatsApp,
    setSendWhatsApp,

    // Draft auto-save display
    isDraftRestored,
    lastSavedAt,
    clearDraft,
    showDraftBanner,
    showAutoSaveIndicator,
    formattedLastSavedAt,
    mobileAutoSaveText,
    filledItemCount,

    // Actions
    handleSaveQuotation,
    handleClearQuotation,
    handleGenerateBillNumber,
  };
};
